<script setup>
import { computed } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useBranding } from 'shared/composables/useBranding';

const props = defineProps({
  id: {
    type: [String, Number],
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  enabled: {
    type: Boolean,
    default: false,
  },
});

const getters = useStoreGetters();
const accountId = getters.getCurrentAccountId;

const { t } = useI18n();
const { replaceInstallationName } = useBranding();

const integrationStatus = computed(() =>
  props.enabled
    ? t('INTEGRATION_APPS.STATUS.ENABLED')
    : t('INTEGRATION_APPS.STATUS.DISABLED')
);

const actionURL = computed(() =>
  frontendURL(`accounts/${accountId.value}/settings/integrations/${props.id}`)
);

const integrationIconMap = {
  webhook: 'i-ph-webhooks-logo-bold',
  dashboard_apps: 'i-ph-squares-four-bold',
  openai: 'i-ph-brain-bold',
  dialogflow: 'i-ph-robot-bold',
  google_translate: 'i-ph-translate-bold',
  dyte: 'i-ph-video-camera-bold',
  leadsquared: 'i-ph-chart-line-up-bold',
  slack: 'i-ph-slack-logo-bold',
  linear: 'i-ph-kanban-bold',
  shopify: 'i-ph-shopping-bag-bold',
  notion: 'i-ph-note-bold',
  captain: 'i-ph-sparkle-bold',
};

const integrationIcon = computed(() => {
  return integrationIconMap[props.id] || 'i-ph-puzzle-piece-bold';
});
</script>

<template>
  <router-link :to="actionURL" class="integration-card group">
    <!-- Card Header -->
    <div class="flex items-start justify-between mb-4">
      <div class="integration-logo">
        <span :class="integrationIcon" class="size-6 integration-icon" />
      </div>
      <div class="status-indicator" :class="enabled ? 'enabled' : 'disabled'">
        <span v-if="enabled" class="i-ph-check-bold size-3" />
        <span v-else class="i-ph-x-bold size-3" />
      </div>
    </div>

    <!-- Card Content -->
    <div class="flex-1">
      <h3 class="text-base font-semibold mb-2" style="color: #111827">
        {{ name }}
      </h3>
      <p class="text-sm line-clamp-2" style="color: #6b7280">
        {{ replaceInstallationName(description) }}
      </p>
    </div>

    <!-- Card Footer -->
    <div class="card-footer">
      <span
        class="status-text"
        :style="enabled ? 'color:#059669' : 'color:#9ca3af'"
      >
        {{ integrationStatus }}
      </span>
      <span class="configure-link"> Configure → </span>
    </div>
  </router-link>
</template>

<style scoped>
.integration-card {
  display: flex;
  flex-direction: column;
  padding: 20px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.2s ease;
  text-decoration: none;
  min-height: 160px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.integration-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 8px 20px -8px rgba(15, 23, 42, 0.15);
  transform: translateY(-1px);
}

.integration-logo {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 8px;
}

.integration-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.integration-icon {
  color: #111827 !important;
}

.status-indicator {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.status-indicator.enabled {
  background: #ecfdf5;
  color: #059669;
  border: 1px solid #a7f3d0;
}

.status-indicator.disabled {
  background: #f3f4f6;
  color: #9ca3af;
  border: 1px solid #e5e7eb;
}

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #f3f4f6;
}

.status-text {
  font-size: 12px;
  font-weight: 500;
}

.configure-link {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
  transition: color 0.15s ease;
}

.integration-card:hover .configure-link {
  color: #0066ff;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
